import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import './NGOSelector.css';

const NGOSelector = ({ ngos, selectedIds, onSelectionChange, maxSelection = 3 }) => {
  const handleNGOClick = (ngoId) => {
    if (selectedIds.includes(ngoId)) {
      // Remove from selection
      onSelectionChange(selectedIds.filter(id => id !== ngoId));
    } else if (selectedIds.length < maxSelection) {
      // Add to selection
      onSelectionChange([...selectedIds, ngoId]);
    }
  };

  // Categorize NGOs
  const categorizeNGOs = () => {
    const categories = {
      people: [],
      environment: [],
      animals: []
    };
    
    ngos.forEach(ngo => {
      if (['Save the Children', 'International Rescue Committee', 'Helen Keller International'].includes(ngo.name)) {
        categories.people.push(ngo);
      } else if (['Rainforest Alliance', '350.org', 'Earthwatch Institute'].includes(ngo.name)) {
        categories.environment.push(ngo);
      } else if (['World Animal Protection', 'Humane Society International', 'International Fund for Animal Welfare'].includes(ngo.name)) {
        categories.animals.push(ngo);
      }
    });
    
    return categories;
  };

  const categorizedNGOs = categorizeNGOs();

  const renderNGOCard = (ngo, index) => {
    const isSelected = selectedIds.includes(ngo._id);
    const isDisabled = !isSelected && selectedIds.length >= maxSelection;
    
    return (
      <motion.div
        key={ngo._id}
        className={`ngo-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
        onClick={() => !isDisabled && handleNGOClick(ngo._id)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        whileHover={!isDisabled ? { y: -5, scale: 1.02 } : {}}
        whileTap={!isDisabled ? { scale: 0.98 } : {}}
      >
        <div className="ngo-card-header">
          <div className="ngo-icon">
            {getOrgIcon(ngo.name)}
          </div>
          <div className={`selection-indicator ${isSelected ? 'selected' : ''}`}>
            {isSelected && <Check size={16} />}
          </div>
        </div>
        
        <div className="ngo-card-content">
          <h3 className="ngo-name">{ngo.name}</h3>
          <p className="ngo-description">{ngo.description}</p>
          {ngo.website && (
            <div className="ngo-website">
              <a 
                href={ngo.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="website-link"
                onClick={(e) => e.stopPropagation()}
              >
                🌐 Website
              </a>
            </div>
          )}
          <div className="ngo-address">
            <span className="address-label">Donation Address:</span>
            <span className="address-value">
              {ngo.address.slice(0, 6)}...{ngo.address.slice(-4)}
            </span>
          </div>
        </div>
        
        {isSelected && (
          <motion.div
            className="selected-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </motion.div>
    );
  };

  return (
    <div className="ngo-selector">
      <div className="selection-counter">
        <span className="counter-text">
          Selected: {selectedIds.length} / {maxSelection}
        </span>
        {selectedIds.length === maxSelection && (
          <span className="counter-complete">✓ Complete</span>
        )}
      </div>
      
      <div className="ngo-categories">
        <div className="category-column">
          <div className="category-header">
            <h4>👥 People</h4>
            <p>Supporting human welfare and rights</p>
          </div>
          <div className="category-ngos">
            {categorizedNGOs.people.map((ngo, index) => renderNGOCard(ngo, index))}
          </div>
        </div>
        
        <div className="category-column">
          <div className="category-header">
            <h4>🌱 Environment</h4>
            <p>Protecting our planet and climate</p>
          </div>
          <div className="category-ngos">
            {categorizedNGOs.environment.map((ngo, index) => renderNGOCard(ngo, index + 3))}
          </div>
        </div>
        
        <div className="category-column">
          <div className="category-header">
            <h4>🐾 Animals</h4>
            <p>Defending animal rights and welfare</p>
          </div>
          <div className="category-ngos">
            {categorizedNGOs.animals.map((ngo, index) => renderNGOCard(ngo, index + 6))}
          </div>
        </div>
      </div>
      
      {selectedIds.length > 0 && (
        <motion.div
          className="selected-summary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h4>You're supporting:</h4>
          <div className="selected-ngos-list">
            {selectedIds.map(ngoId => {
              const ngo = ngos.find(n => n._id === ngoId);
              return ngo ? (
                <motion.div
                  key={ngoId}
                  className="selected-ngo-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <span className="selected-ngo-icon">
                    {getOrgIcon(ngo.name)}
                  </span>
                  <span className="selected-ngo-name">{ngo.name}</span>
                </motion.div>
              ) : null;
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Helper function to get appropriate icons for different NGOs by category
const getOrgIcon = (name) => {
  const icons = {
    // 🌍 For People Worldwide
    'Save the Children': '👶',
    'International Rescue Committee': '🏕️',
    'Helen Keller International': '👁️',
    
    // 🌱 For Environmental Protection  
    'Rainforest Alliance': '🐸',
    '350.org': '🌡️',
    'Earthwatch Institute': '🔬',
    
    // 🐾 For Animal Protection
    'World Animal Protection': '🐘',
    'Humane Society International': '🐶',
    'International Fund for Animal Welfare': '🐻'
  };
  
  return icons[name] || '🤝';
};

export default NGOSelector;